import { lazy, type ComponentType } from "react";

/** A PAGE THAT SURVIVES A DEPLOY (2026-09-22).
 *
 *  Every page but Home is its own hashed chunk, fetched when you navigate to it. Deploying replaces
 *  those hashes, so a tab that was open before the deploy asks for a file that is no longer there --
 *  and because the site's SPA rule answers every unknown path with index.html, the browser gets HTML
 *  where it expected JavaScript and throws "Failed to fetch dynamically imported module". What the
 *  manager saw was React Router's raw developer error screen on a page that had been working.
 *
 *  A missing chunk means one thing: this tab is running yesterday's app. So reload once -- that
 *  fetches the new index.html with the new hashes -- and only treat a second failure as a real
 *  error, which the route's error screen then explains. The guard is a timestamp rather than a flag
 *  so a tab left open for a week can still recover the next time. */
const KEY = "orator:chunk-reload";
const COOLDOWN_MS = 20_000;

export function chunkRecover(err: unknown): never {
  let last = 0;
  try { last = Number(sessionStorage.getItem(KEY) || 0); } catch { /* private mode: reload once */ }
  if (Date.now() - last > COOLDOWN_MS) {
    try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
    window.location.reload();
    // The reload is in flight; never resolve, so nothing renders a half-loaded page in the meantime.
    throw new Promise(() => {});
  }
  throw err;
}

/** lazy(), with the deploy case handled. */
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => load().catch(chunkRecover));
}
